import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { getImageFile } from 'utils/stringUtils';
import ClassNames from 'classnames';
import ButtonBase from '@material-ui/core/ButtonBase';
import { useHistory, useLocation } from 'react-router-dom';
import Badge from 'components/Badge';
import { emitter } from '@marvelapp/react-ab-test';
import getServiceType from '../utils/getServiceType';
import getFormattedType from '../utils/getFormattedType';
import PageContext from '../PageContext';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    display: 'block',
  },
  root: {
    background: '#fff',
    boxShadow: '0 0 29px 0 rgba(0,0,0,0.10)',
    borderRadius: 4,
    width: '100%',
    height: 84,
    marginBottom: 19,
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
  },
  last: {
    marginBottom: 0,
  },
  imageBg: {
    width: 122,
    height: 84,
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    padding: '3px 10px',
    width: '100%',
  },
  title: {
    fontFamily: 'PoppinsSemiBold',
    fontSize: 16,
    letterSpacing: 0,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  description: {
    fontSize: 12,
    letterSpacing: 0,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    height: 15,
  },
  longDescription: {
    '-webkit-line-clamp': '2',
  },
  longTitle: {
    '-webkit-line-clamp': '2',
    maxHeight: 55,
  },
  ratingWrapper: {
    '& div': {
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
  badgeWrapper: {
    '& div': {
      width: '100%',
      margin: 0,
      height: 20,
    },
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
}));

function ResourceCard({ data, isLast }) {
  const classes = useStyles();
  const type = getServiceType(data);
  const { landingInfo, sendMixpanelEvent } = useContext(PageContext);
  const activeVariant = emitter.getActiveVariant('hero-variant');
  const isVariantA = activeVariant === 'variantA';
  const history = useHistory();
  const location = useLocation();
  const imageSrc = getImageFile(data);
  const {
    slug,
    title,
    name,
    article,
    url,
    description,
    subtitle,
    shortDescription,
  } = data;
  const link = `/${type}/${slug}`;
  const trackType = getFormattedType(data);
  const redirect = () => {
    if (type === 'source' || type === 'link') {
      const isInternal =
        url && (url.includes('crediblemind.com') || url.startsWith('/'));
      if (isInternal) {
        sendMixpanelEvent('Client Resource Card Clicked - Home', {
          article: name || title || article,
          type: trackType,
          slug,
          resource: url,
        });
        history.push(`/articles/${slug}`);
      } else {
        sendMixpanelEvent('Client Resource Card Exited - Home', {
          name: name || title || article,
          type: trackType,
          slug,
          exitPage: url,
        });
        window.open(url, '_blank', 'noopener');
      }
    } else {
      const pageName = location.pathname.split('/')[1];
      const page = pageName ? `${pageName}Page` : 'homePage';

      sendMixpanelEvent('Resource Card - Clicked', {
        name: name || title || article,
        type: trackType,
        page,
        path: 'banner',
        slug,
      });
      history.push(link);
    }
  };
  const isAssessment = trackType === 'assessment';
  const hasNoRating = true;
  const showDescription =
    description &&
    typeof description === 'string' &&
    !isVariantA &&
    !isAssessment;

  const shouldApplyGrayscaleFilter = _get(
    landingInfo,
    'pageText.grayscale',
    false,
  );

  return (
    <ButtonBase onClick={redirect} disableRipple className={classes.container}>
      <Grid
        item
        xs={12}
        className={ClassNames(classes.root, { [classes.last]: isLast })}
      >
        <div>
          <div
            className={ClassNames(classes.imageBg, {
              [classes.grayscale]: shouldApplyGrayscaleFilter,
            })}
            style={{
              background: `url(${imageSrc}) no-repeat center / cover`,
            }}
          ></div>
        </div>

        <div
          className={ClassNames(classes.info, {
            [classes.centered]: hasNoRating,
          })}
        >
          <div className={classes.badgeWrapper}>
            <Badge type={trackType} data={data} />
          </div>
          <div>
            <Typography
              className={ClassNames(classes.title, {
                [classes.longTitle]: hasNoRating && !isAssessment,
              })}
              color="textPrimary"
              align="left"
            >
              {isAssessment ? name : title || name || article}
            </Typography>
          </div>
          {showDescription && !isAssessment && (
            <div>
              <Typography
                className={ClassNames(classes.description, {
                  [classes.longDescription]: activeVariant === 'variantB',
                })}
                color="textPrimary"
                align="left"
              >
                {description}
              </Typography>
            </div>
          )}
          {(type === 'link' || type === 'list') && (
            <div>
              <Typography
                className={ClassNames(
                  classes.description,
                  classes.longDescription,
                )}
                color="textPrimary"
                align="left"
              >
                {name || subtitle || shortDescription}
              </Typography>
            </div>
          )}
        </div>
      </Grid>
    </ButtonBase>
  );
}
ResourceCard.propTypes = {
  data: PropTypes.object,
  isLast: PropTypes.bool,
};

export default ResourceCard;
